import React from "react";

import type { Answer } from "~/feedback/FeedbackItem/Question";
import type { Question } from "~/feedback/ResponseForm/validation";
import store from "~/lib/store";
import { linkRegexInline, simpleHash } from "~/lib/utils";

import type { FeedbackTemplateTypes } from "~/__generated__/TemplateBrowserQuery.graphql";

const listRoutes = "inbox|requested|pending|given|drafts|team|shared|aboutme";

export const LIST_ROUTE_PATH = `/feedback/:filter(${listRoutes})/`;
export const LIST_ROUTE_REGEX = `/feedback/(${listRoutes})/`;

export type FeedbackRequestContextType = "report" | "event" | "myself" | "other";

export enum FeedbackFilter {
	INBOX = "inbox",
	REQUESTED = "requested",
	PENDING = "pending",
	GIVEN = "given",
	DRAFTS = "drafts",
	TEAM = "team",
	SHARED = "shared",
	ABOUT_ME = "aboutme",
}

// This enum is mirrored on the python side in apps/feedback/models.py
export const ThreeSixtyRelation = {
	self: 2,
	manager: 1,
	peers: 3,
	directReports: 4,
} as const;

// This enum is mirrored on the python side in apps/feedback/models.py
export const ThreeSixtyRelationSingularReverse = {
	"2": "Self",
	"1": "Manager",
	"3": "Peer",
	"4": "Direct Report",
} as const;

// This enum is mirrored on the python side in apps/feedback/models.py
export const ThreeSixtyRelationPluralReverse = {
	"2": "Self",
	"1": "Manager",
	"3": "Peers",
	"4": "Direct Reports",
} as const;

export function isFeedbackFilter(filter: string): filter is FeedbackFilter {
	switch (filter) {
		case "inbox":
		case "requested":
		case "pending":
		case "given":
		case "drafts":
		case "team":
		case "shared":
		case "aboutme":
			return true;
		default:
			return false;
	}
}

export function feedbackFilterToGraphQL(filter: FeedbackFilter): keyof typeof FeedbackFilter {
	switch (filter) {
		case FeedbackFilter.INBOX:
			return "INBOX";
		case FeedbackFilter.REQUESTED:
			return "REQUESTED";
		case FeedbackFilter.PENDING:
			return "PENDING";
		case FeedbackFilter.GIVEN:
			return "GIVEN";
		case FeedbackFilter.DRAFTS:
			return "DRAFTS";
		case FeedbackFilter.TEAM:
			return "TEAM";
		case FeedbackFilter.SHARED:
			return "SHARED";
		case FeedbackFilter.ABOUT_ME:
			return "ABOUT_ME";
	}
}

export function replaceLinks(textToSearch: string, extraAnchorProps?: React.HTMLProps<HTMLAnchorElement>) {
	const anchorProps = extraAnchorProps ?? {};
	let match: RegExpMatchArray | null = null;
	let matches = new Array<RegExpMatchArray>();
	let matchIndices: [number, number][] = []; //holds the [start, end] indices of each link matched by the regex
	let linkFormattedElement = <React.Fragment>{textToSearch}</React.Fragment>;

	while ((match = linkRegexInline.exec(textToSearch)) && match.index !== undefined) {
		matches.push(match);
		matchIndices.push([match.index, match.index + match[1].length]);
	}

	if (matches.length > 0) {
		linkFormattedElement = (
			<React.Fragment>
				{
					/* Get the first part of the feedback text, before any link.
						This is the substring from 0 to the start of the first match ([0][0]) */
					matchIndices[0] !== undefined && matchIndices[0][0] !== undefined
						? textToSearch.substring(0, matchIndices[0][0])
						: ""
				}
				{matches.map((match, i: number) => {
					//iterate through each link matched by the regex
					return (
						<React.Fragment key={match.index}>
							<a href={match[1]} target="_blank" rel="noopener noreferrer" {...anchorProps}>
								{match[1]}
							</a>
							{
								/* Find the text between the end of the current link ([i][1]) and the beginning of the next ([i+1][0]).
									If there is no next link, finish at the end of the text. */
								i <= matches.length - 2 && matchIndices[i][1] !== undefined && matchIndices[i + 1][0] !== undefined
									? textToSearch.substring(matchIndices[i][1], matchIndices[i + 1][0])
									: textToSearch.substring(matchIndices[i][1], textToSearch.length)
							}
						</React.Fragment>
					);
				})}
			</React.Fragment>
		);
	}

	return linkFormattedElement;
}

export function formatFeedbackTemplateType(type: FeedbackTemplateTypes) {
	switch (type) {
		case "MEETING":
			return "Meeting";
		case "SOMETHING_ELSE":
			return "Something else";
		case "SOMEONE":
			return "Someone";
		case "UNSOLICITED":
			return "Give feedback";
		default:
			return "";
	}
}

export const feedbackStore = store.namespace("feedback");

export function questionAnswerToText(question: Question, answer: Answer) {
	let answerString = "";

	if (question.type === "text" && typeof answer.value === "string") {
		answerString = answer.value;
	} else if (question.type === "choice") {
		if (Array.isArray(answer.value)) {
			if (question.appearance === "checkbox") {
				answerString = answer.value.map(value => value.label).join(", ");
			}
		} else if (typeof answer.value === "object") {
			if (question.appearance === "radio") {
				answerString = answer.value.label;
			} else if (question.appearance === "smiles") {
				answerString = `${answer.value.value}`;
			} else if (question.appearance === "stars" && typeof answer.value.value === "number") {
				answerString = `${answer.value.value / 2}`;
			} else if (question.appearance === "thumbs" && typeof answer.value.value === "number") {
				answerString = `${answer.value.value / 10}`;
			}
		}
	}

	return answerString;
}

export function anonymizeFeedbackUser(
	feedbackId: string,
	user: { id: string },
): { department: string; title: string; firstName: string; lastName: string; fullName: string; id: string } {
	// Not the same as backend. only used on viewer.user
	const hash = Math.abs(simpleHash(feedbackId + user.id))
		.toString(16)
		.substr(0, 6);
	return {
		id: hash,
		fullName: `anonymous_${hash}`,
		firstName: "anonymous",
		lastName: hash,
		department: "",
		title: "",
	};
}
